import { OnInit, Component, Input } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MainState } from "@app/store/state";
import { EquipmentItemService } from "@core/services/equipment-item.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { EquipmentApiService } from "@features/equipment/services/equipment-api.service";
import { EquipmentActionTypes, RejectEquipmentItemEditProposal } from "@features/equipment/store/equipment.actions";
import { EditProposalInterface } from "@features/equipment/types/edit-proposal.interface";
import { EquipmentItemBaseInterface, EquipmentItemType } from "@features/equipment/types/equipment-item-base.interface";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { map, switchMap, take } from "rxjs/operators";

@Component({
  selector: "astrobin-reject-edit-proposal-modal",
  templateUrl: "./reject-edit-proposal-modal.component.html",
  styleUrls: ["./reject-edit-proposal-modal.component.scss"]
})
export class RejectEditProposalModalComponent extends BaseComponentDirective implements OnInit {
  fields: FormlyFieldConfig[];
  form: FormGroup = new FormGroup({});
  model: {
    comment: string;
  } = {
    comment: null
  };

  @Input()
  editProposal: EditProposalInterface<EquipmentItemBaseInterface>;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly loadingService: LoadingService,
    public readonly translateService: TranslateService,
    public readonly modal: NgbActiveModal,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly equipmentApiService: EquipmentApiService,
    public readonly equipmentItemService: EquipmentItemService
  ) {
    super(store$);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.fields = [
      {
        key: "comment",
        type: "textarea",
        id: "comment",
        wrappers: ["default-wrapper"],
        props: {
          label: this.translateService.instant("Comment"),
          required: false,
          rows: 4
        }
      }
    ];
  }

  reject() {
    this.loadingService.setLoading(true);

    const comment: string = this.form.get("comment").value;

    this.store$.dispatch(
      new RejectEquipmentItemEditProposal({
        editProposal: this.editProposal,
        comment
      })
    );

    const type: EquipmentItemType = this.equipmentItemService.getType(this.editProposal);

    this.actions$
      .pipe(
        ofType(EquipmentActionTypes.REJECT_EQUIPMENT_ITEM_EDIT_PROPOSAL_SUCCESS),
        take(1),
        switchMap(editProposal =>
          this.equipmentApiService
            .releaseEditProposalReviewLock(type, this.editProposal.id)
            .pipe(map(() => editProposal))
        )
      )
      .subscribe(() => {
        this.popNotificationsService.success(
          this.translateService.instant("This edit proposal has been rejected."),
          this.translateService.instant("Thank you so much for contributing to the AstroBin equipment database! 🙌")
        );
        this.loadingService.setLoading(false);
        this.modal.close();
      });
  }

  cancel() {
    const type: EquipmentItemType = this.equipmentItemService.getType(this.editProposal);
    this.equipmentApiService.releaseEditProposalReviewLock(type, this.editProposal.id).subscribe(() => {
      this.modal.dismiss();
    });
  }
}
